<template>
  <div
    role="main"
    aria-label="Liste des bons de retour inter-agence"
    tabindex="-1"
  >
    <!-- Recherche et actions -->
    <b-row>
      <b-col>
        <div
          class="custom-search d-flex justify-content-end"
          aria-label="Actions principales"
        >
          <b-form-group>
            <b-button
              v-if="user.role === 'admin'"
              :to="{ name: 'bon-retour-inter-agence-add' }"
              variant="gradient-primary"
              class="ml-1 d-inline-block"
              aria-label="Créer un nouveau bon de retour inter-agence"
            >
              <feather-icon
                icon="PlusIcon"
                aria-hidden="true"
              />
              <span class="ml-1">Nouveau retour inter-agence</span>
            </b-button>
          </b-form-group>
        </div>
      </b-col>
    </b-row>
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        label="Chargement en cours"
      />
    </div>
    <!-- Table des bons de retour inter-agence -->
    <vue-good-table
      :columns="tableColumns"
      :rows="returnVouchers"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
      aria-label="Tableau des bons de retour inter-agence"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Colonne: Action -->
        <span v-if="props.column.field === 'action'">
          <span>
            <router-link
              class="text-info"
              :to="{ name: 'bon-retour-inter-agence-show',
                     params: { id: props.row.id} }"
              aria-label="Voir le détail du bon de retour inter-agence"
            >
              <feather-icon
                :id="`invoice-row-${props.row.id}-show-icon`"
                icon="EyeIcon"
                class="cursor-pointer mr-1"
                size="16"
                aria-hidden="true"
              />
            </router-link>
            <b-tooltip
              title="Voir le détail"
              class="cursor-pointer"
              :target="`invoice-row-${props.row.id}-show-icon`"
            />
          </span>
          <span>
            <router-link
              v-if="!props.row.is_validated && props.row.destination_repository.id === user.repository"
              class="text-success"
              :to="{ name: 'bon-retour-inter-agence-validate',
                     params: { id: props.row.id} ,}"
            >
              <feather-icon
                :id="`invoice-row-${props.row.id}-show-icon`"
                icon="CheckSquareIcon"
                class="cursor-pointer mr-1"
                size="16"
                @click="
                  $router.push({
                    name: 'bon-retour-inter-agence-validate',
                    params: { id: props.row.id} ,
                  })
                "
              />
            </router-link>
            <b-tooltip
              title="Détail"
              class="cursor-pointer"
              :target="`invoice-row-${props.row.id}-show-icon`"
            />
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div
          class="d-flex justify-content-between flex-wrap"
          aria-label="Pagination des bons de retour inter-agence"
        >
          <div class="d-flex align-items-center mb-0 mt-1">
            <span
              class="text-nowrap"
              aria-hidden="true"
            >Affichage 1 à</span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              aria-label="Nombre de résultats par page"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap "> de {{ count }} bons de retour inter-agence</span>
          </div>
          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              aria-label="Changer de page"
              @input="pageNext(currentPage)"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BPagination, BFormGroup, BFormSelect, BButton,
  BSpinner, BTooltip, BCol, BRow,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// import vSelect from 'vue-select'
import storeAuth from '@/store/store'

export default {
  name: 'ListInterAgency',
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    BTooltip,
    // vSelect,
    BCol,
    BRow,
  },
  data() {
    return {
      size: 40,
      isLoading: false,
      pageLength: 100,
      dir: false,
      columns: [
        {
          label: 'Référence retour',
          field: 'id',
        },
        {
          label: 'Nombre de colis',
          field: 'orders_count',
        },
        {
          label: 'Total TTC (TND)',
          field: 'total_price',
          formatFn: value => `${value.toFixed(3)} TND`,
        },
        {
          label: 'Dépôt source',
          field: 'source_repository.name',
        },
        {
          label: 'Dépôt destination',
          field: 'destination_repository.name',
        },
        {
          label: 'Date et heure',
          field: 'created_at',
          formatFn: this.formatFn,
        },
        {
          label: 'Actions',
          field: 'action',
        },
      ],
      returnVouchers: [],
      searchTerm: '',
      currentPage: 1,
      count: 0,
      user: {},
      repositories: [],
      destinationRepository: '',
    }
  },
  computed: {
    tableColumns() {
      if (this.user.role === 'admin') {
        return this.columns
      }
      return this.columns.filter(column => column.field !== 'source_repository')
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getReturnVouchers(1)
    this.getRepositories()
  },
  methods: {
    async pageNext(currentPage) {
      if (this.supplier !== '') {
        // test
      } else {
        this.getReturnVouchers(currentPage)
      }
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    async getReturnVouchers(currentPage) {
      this.isLoading = true
      const { data } = await axios.get('api/return-vouchers/inter-agency-return', { params: { p: currentPage } })
      this.returnVouchers = data.results
      this.count = data.count
      this.isLoading = false
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    async getRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
